import type { Cents } from './money';

/**
 * UI types for the Payroll Accounting Export module (contract section 15 of docs/payroll-accounting-export-contract.md).
 * Every money member is INTEGER CENTS (`...Cents`); wireMapping.ts is the only place wire decimals are turned into cents.
 * Enums travel as strings.
 */

export type ExportMode = 'Compound' | 'PerEmployee';
export type ExportKind = 'Original' | 'Reversal' | 'Replacement';
export type ExportStatus = 'Draft' | 'ValidationFailed' | 'Ready' | 'Generated' | 'Downloaded' | 'Superseded' | 'ReversalRequired' | 'Reversed' | 'Failed';
/** What the eligible-payrolls list says about a run's export. */
export type EligibleExportStatus = 'None' | 'Generated' | 'Downloaded' | 'ReversalRequired' | 'Reversed' | 'Superseded';
export type JournalSide = 'Debit' | 'Credit';
export type ChartAccountType = 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense' | 'Clearing';
export type MappingTreatment = 'Post' | 'NonPosting';
export type CoverageStatus = 'Supplied' | 'NotSupplied' | 'PartiallySupplied';
export type UnmappedReason = 'NoMapping' | 'Ambiguous' | 'ArchivedAccount';

/** Contract (docs/payroll-accounting-export-mapping-options-contract.md) section 1: PascalCase string members on the wire. */
export type NetPayDetailMode = 'Consolidated' | 'PerEmployee';
/** `LegacyResolver` is today's behavior (the 5-level resolver): the implicit default and the recorded value of every export made before options existed. Never sent. */
export type WageMappingMode = 'SingleAccount' | 'GroupBased' | 'LegacyResolver';
export type WageGroupDimension = 'Department' | 'WorkLocation' | 'Role' | 'Discipline' | 'EmploymentType' | 'EmployeeGroup';
export type OptionSource = 'Request' | 'CompanySettings' | 'LegacyDefault';

export const NET_PAY_DETAIL_MODES: readonly NetPayDetailMode[] = ['PerEmployee', 'Consolidated'];
/** The modes a person can choose (LegacyResolver is read-only). */
export const SELECTABLE_WAGE_MODES: readonly Exclude<WageMappingMode, 'LegacyResolver'>[] = ['SingleAccount', 'GroupBased'];
export const WAGE_GROUP_DIMENSIONS: readonly WageGroupDimension[] = ['Department', 'WorkLocation', 'Role', 'Discipline', 'EmploymentType', 'EmployeeGroup'];

export const CHART_ACCOUNT_TYPES: readonly ChartAccountType[] = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense', 'Clearing'];

/** What the signed-in person can do here, as the server says it (a hint for what to offer; the server enforces every action). */
export interface AccountingExportCapabilities {
  canPreview: boolean;
  canGenerate: boolean;
  canViewPerEmployee: boolean;
  canManageMappings: boolean;
  canManageChartOfAccounts: boolean;
  canReverse: boolean;
  canViewAudit: boolean;
}

export interface PayScheduleOption {
  id: string | null;
  label: string;
}

export interface EligiblePayroll {
  payrollRunId: string;
  payScheduleId: string | null;
  payScheduleLabel: string;
  periodStart: string;
  periodEnd: string;
  checkDate: string;
  employeeCount: number;
  grossEarningsCents: Cents;
  netPayCents: Cents;
  exportStatus: EligibleExportStatus;
}

export interface EligiblePayrolls {
  items: EligiblePayroll[];
  payScheduleOptions: PayScheduleOption[];
}

export interface EligiblePayrollParams {
  payScheduleId?: string;
  from?: string;
  to?: string;
}

/** The export options the person chose for one request; an omitted member means "use the company setting". */
export interface ExportOptionsInput {
  netPayDetailMode?: NetPayDetailMode | undefined;
  wageMappingMode?: Exclude<WageMappingMode, 'LegacyResolver'> | undefined;
  wageGroupDimension?: WageGroupDimension | undefined;
}

/** The effective options of a preview or an export. `recorded: false` marks an export made before options existed (the values are then the legacy defaults, not a record). */
export interface ExportOptions {
  netPayDetailMode: NetPayDetailMode | null;
  wageMappingMode: WageMappingMode | null;
  wageGroupDimension: WageGroupDimension | null;
  source: OptionSource | null;
  recorded: boolean;
  settingsVersion: number | null;
  groupConfigVersion: number | null;
}

export interface PreviewEmployee {
  employeeId: string;
  employeeReference: string | null;
  /** Present only when the caller holds the per-employee permissions. */
  displayName: string | null;
  department: string | null;
  location: string | null;
  grossEarningsCents: Cents;
  netPayCents: Cents;
}

export interface JournalLine {
  lineNumber: number;
  side: JournalSide;
  amountCents: Cents;
  accountNumber: string;
  accountName: string;
  accountType: string;
  category: string;
  subcategory: string | null;
  payrollCode: string | null;
  employeeReference: string | null;
  department: string | null;
  location: string | null;
  classDimension: string | null;
  checkReference: string | null;
  description: string;
  mappingId: string;
  mappingVersion: number;
  sourceLineIds: string[];
  /** Wage lines under a group mapping: which dimension and group the line was resolved by, and why (plain text). Null otherwise. */
  groupDimension: WageGroupDimension | null;
  groupKey: string | null;
  groupLabel: string | null;
  resolutionLevel: string | null;
  resolutionReason: string | null;
}

export interface Journal {
  reference: string;
  totalDebitsCents: Cents;
  totalCreditsCents: Cents;
  differenceCents: Cents;
  isBalanced: boolean;
  lines: JournalLine[];
}

export interface ReconciliationFigures {
  finalizedGrossCents: Cents;
  employeeTaxesCents: Cents;
  employerTaxesCents: Cents;
  preTaxDeductionsCents: Cents;
  postTaxDeductionsCents: Cents;
  garnishmentsCents: Cents;
  /** `null` means the payroll calculation does not supply employer contributions: never shown as zero. */
  employerContributionsCents: Cents | null;
  netPayCents: Cents;
  totalPayrollCostCents: Cents;
  journalDebitsCents: Cents;
  journalCreditsCents: Cents;
  unmappedAmountCents: Cents;
  excludedAmountCents: Cents;
  differenceCents: Cents;
  isComplete: boolean;
}

export type SourceProvenance = 'Calculation' | 'Correction';

export interface SourceLine {
  id: string;
  calculationId: string;
  employeeReference: string | null;
  category: string;
  subcategory: string | null;
  payrollCode: string | null;
  jurisdiction: string | null;
  side: JournalSide;
  amountCents: Cents;
  provenance: SourceProvenance;
  posted: boolean;
}

export interface UnmappedItem {
  category: string;
  subcategory: string | null;
  payrollCode: string | null;
  lineCount: number;
  amountCents: Cents;
  reason: UnmappedReason;
}

export interface ExcludedItem {
  category: string;
  amountCents: Cents;
  reason: string;
  mappingId: string;
}

export interface ExportMessage {
  code: string;
  message: string;
}

export interface SourceCoverageItem {
  category: string;
  status: CoverageStatus;
  note: string;
}

export interface ExistingExportRef {
  id: string;
  status: ExportStatus;
}

export interface AccountRef {
  accountNumber: string;
  accountName: string;
}

export interface PreviewConfiguration {
  options: ExportOptions;
  mappingVersionMax: number | null;
  exportKeyVersion: number | null;
}

export interface PreviewSummary {
  employeeCount: number | null;
  groupCount: number | null;
  earningCodeCount: number | null;
  deductionCodeCount: number | null;
  mappedComponents: number | null;
  missingMappings: number | null;
  ambiguousMappings: number | null;
  totalDebitsCents: Cents | null;
  totalCreditsCents: Cents | null;
  varianceCents: Cents | null;
}

export interface WageGroupLine {
  groupKey: string;
  label: string;
  employeeCount: number;
  grossAmountCents: Cents;
  account: AccountRef | null;
  mappingId: string | null;
  mappingVersion: number | null;
  reason: string | null;
}

/** One row of the taxes-by-type, deductions-by-category and employer-contributions breakdowns. */
export interface BreakdownLine {
  key: string;
  label: string;
  amountCents: Cents;
  account: AccountRef | null;
  note: string | null;
}

export interface EmployerContributions {
  /** True when the payroll calculation does not supply employer contributions: never shown as zero. */
  notSupplied: boolean;
  items: BreakdownLine[];
}

/** The non-exported reconciliation drill-down of a Consolidated net pay line. Only holders of the per-employee permissions receive it. */
export interface NetPayDetailLine {
  employeeReference: string;
  netPayCents: Cents;
}

export interface UnusedWageMappings {
  count: number;
  note: string | null;
}

export interface ExportPreview {
  payrollRunId: string;
  mode: ExportMode;
  accountingDate: string;
  status: 'Ready' | 'ValidationFailed';
  currency: string;
  employees: PreviewEmployee[];
  journal: Journal | null;
  reconciliation: ReconciliationFigures | null;
  sourceLines: SourceLine[];
  unmapped: UnmappedItem[];
  excluded: ExcludedItem[];
  warnings: ExportMessage[];
  blockers: ExportMessage[];
  sourceCoverage: SourceCoverageItem[];
  existingExport: ExistingExportRef | null;
  options: ExportOptions;
  configuration: PreviewConfiguration | null;
  summary: PreviewSummary | null;
  wageGroups: WageGroupLine[];
  taxesByType: BreakdownLine[];
  deductionsByCategory: BreakdownLine[];
  employerContributions: EmployerContributions | null;
  /** Null unless the net pay line is consolidated AND the caller holds the per-employee permissions. */
  netPayDetail: NetPayDetailLine[] | null;
  unusedWageMappings: UnusedWageMappings | null;
  limitations: string[];
}

export interface PreviewParams extends ExportOptionsInput {
  payrollRunId: string;
  mode: ExportMode;
  accountingDate?: string;
}

export interface CreateExportInput extends ExportOptionsInput {
  payrollRunId: string;
  mode: ExportMode;
  accountingDate?: string;
}

export interface ExportStateChange {
  fromStatus: string | null;
  toStatus: string;
  reason: string | null;
  occurredAtUtc: string;
}

export interface ExportSummary {
  id: string;
  payrollRunId: string;
  kind: ExportKind;
  mode: ExportMode;
  targetFormat: string;
  status: ExportStatus;
  checkDate: string;
  periodStart: string;
  periodEnd: string;
  accountingDate: string;
  journalReference: string;
  totalDebitsCents: Cents;
  totalCreditsCents: Cents;
  lineCount: number;
  checksum: string;
  csvSchemaVersion: number;
  mappingVersionMax: number;
  reversalOfExportId: string | null;
  replacesExportId: string | null;
  reversedByExportId: string | null;
  supersededByExportId: string | null;
  reason: string | null;
  generatedAtUtc: string;
  downloadCount: number;
  firstDownloadedAtUtc: string | null;
  operationId: string;
  options: ExportOptions;
}

export interface ExportDetail extends ExportSummary {
  lines: JournalLine[];
  stateHistory: ExportStateChange[];
}

export interface CreateExportResult {
  export: ExportDetail;
  /** True when the same export already existed: nothing new was created. */
  duplicate: boolean;
}

export interface ListExportsParams {
  runId?: string;
  status?: ExportStatus;
  kind?: ExportKind;
  page?: number;
  pageSize?: number;
}

export interface ReplacementInput {
  reason: string;
  accountingDate?: string;
}

export interface ExportAuditItem {
  id: string;
  occurredAtUtc: string;
  /** Who did it: the audit event carries the user's id and a type (User, System), not their name. An older shape may carry an email snapshot. */
  actorUserId: string | null;
  actorType: string | null;
  actorEmailSnapshot: string | null;
  eventType: string;
  metadataJson: string | null;
}

export interface DownloadedExport {
  blob: Blob;
  fileName: string;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ---- chart of accounts ----

export interface ChartAccount {
  id: string;
  accountNumber: string;
  name: string;
  accountType: ChartAccountType;
  isActive: boolean;
  source: 'Manual' | 'CsvImport';
  externalReference: string | null;
  mappingUseCount: number;
}

export interface ListChartAccountsParams {
  search?: string;
  type?: ChartAccountType;
  active?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateChartAccountInput {
  accountNumber: string;
  name: string;
  accountType: ChartAccountType;
  externalReference?: string;
}

export interface UpdateChartAccountInput {
  name: string;
  externalReference?: string | null;
}

export interface ChartImportError {
  row: number;
  message: string;
}

export interface ChartImportResult {
  dryRun: boolean;
  accepted: number;
  rejected: number;
  errors: ChartImportError[];
}

// ---- mappings ----

export interface AccountingMapping {
  id: string;
  category: string;
  subcategory: string | null;
  payrollCode: string | null;
  employeeId: string | null;
  departmentId: string | null;
  locationCode: string | null;
  /** Group qualifier of a wage mapping (both set or both null); category EarningsGross only. Absent from an older server's answer. */
  groupDimension?: WageGroupDimension | null;
  groupKey?: string | null;
  treatment: MappingTreatment;
  nonPostingReason: string | null;
  chartAccountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  accountType: string | null;
  direction: JournalSide;
  effectiveFrom: string;
  effectiveTo: string | null;
  mappingVersion: number;
  isActive: boolean;
  confirmedFundingModel: boolean;
}

export interface ListMappingsParams {
  category?: string;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateMappingInput {
  category: string;
  subcategory?: string;
  payrollCode?: string;
  employeeId?: string;
  departmentId?: string;
  locationCode?: string;
  groupDimension?: WageGroupDimension;
  groupKey?: string;
  treatment: MappingTreatment;
  nonPostingReason?: string;
  chartAccountId?: string;
  effectiveFrom: string;
  confirmedFundingModel?: boolean;
}

export interface MappingReadiness {
  ready: boolean;
  missing: UnmappedItem[];
  ambiguous: UnmappedItem[];
}

// ---- export settings, employee groups, wage groups and code mappings (docs/payroll-accounting-export-mapping-options-contract.md sections 3 and 6 to 7) ----

/** The default wage or clearing account as the company-default mapping shows it. Accounts are never stored in the settings: one source of truth, the mapping. */
export interface DefaultAccountRef {
  mappingId: string | null;
  accountNumber: string;
  accountName: string;
}

export interface ExportSettings {
  netPayDetailMode: NetPayDetailMode;
  wageMappingMode: WageMappingMode;
  wageGroupDimension: WageGroupDimension | null;
  settingsVersion: number;
  groupConfigVersion: number;
  /** False when the company has never saved settings: the values shown are the legacy defaults. */
  isConfigured: boolean;
  updatedAtUtc: string | null;
  updatedByUserId: string | null;
  defaultWageAccount: DefaultAccountRef | null;
  defaultClearingAccount: DefaultAccountRef | null;
}

export interface SaveExportSettingsInput {
  netPayDetailMode: NetPayDetailMode;
  wageMappingMode: Exclude<WageMappingMode, 'LegacyResolver'>;
  wageGroupDimension?: WageGroupDimension | undefined;
  expectedVersion: number;
}

export interface EmployeeGroup {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  memberCount: number;
  concurrencyVersion: number;
}

export interface CreateEmployeeGroupInput {
  name: string;
  description?: string | undefined;
  effectiveFrom?: string | undefined;
}

export interface UpdateEmployeeGroupInput {
  name: string;
  description?: string | undefined;
  effectiveTo?: string | null | undefined;
  expectedVersion: number;
}

/** A member is shown by employee number only (never a name). */
export interface EmployeeGroupMember {
  membershipId: string;
  employeeReference: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface AddGroupMembersInput {
  employeeIds: string[];
  effectiveFrom: string;
  effectiveTo?: string | undefined;
}

/** Someone who can be added to an employee group (from the Employees service). */
export interface EmployeeCandidate {
  id: string;
  employeeNumber: string;
  name: string;
}

export type WageGroupStatus = 'Mapped' | 'Missing' | 'Ambiguous';

export interface WageGroupRow {
  groupKey: string;
  label: string;
  employeeCount: number;
  status: WageGroupStatus;
  account: AccountRef | null;
  mappingId: string | null;
  mappingVersion: number | null;
}

export interface WageGroupGrid {
  dimension: WageGroupDimension;
  accountingDate: string | null;
  groups: WageGroupRow[];
  employeesWithoutGroup: number;
}

export interface WageGroupParams {
  payrollRunId: string;
  dimension: WageGroupDimension;
  accountingDate?: string | undefined;
}

export type CodeKind = 'Deduction' | 'Earning' | 'Benefit';
export type CodeMappingStatus = 'Mapped' | 'Unmapped' | 'Ambiguous';
export type CatalogAvailability = 'Available' | 'Unavailable';

export interface CodeMappingRef {
  id: string;
  accountNumber: string;
  accountName: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  version: number | null;
}

export interface CodeReadinessItem {
  kind: CodeKind;
  codeId: string;
  code: string | null;
  name: string;
  categoryCode: string | null;
  classification: string | null;
  /** Informational only: the mapping never changes or recalculates tax treatment. */
  taxTreatment: 'PreTax' | 'PostTax' | null;
  source: 'Employee' | 'Employer' | null;
  accountingCategory: string;
  payrollCode: string;
  mappingStatus: CodeMappingStatus;
  mapping: CodeMappingRef | null;
  isActive: boolean;
  /** Employees holding an effective election for the code on the check date; null when not asked for or unavailable. */
  employeesWithElection: number | null;
  /** Always null today: the payroll supplies no per-code amounts. */
  totalAmountInPayroll: Cents | null;
  unavailableReason: string | null;
}

export interface CodeReadiness {
  items: CodeReadinessItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  catalogs: { deductions: CatalogAvailability; earnings: CatalogAvailability; benefits: CatalogAvailability };
  appliesToJournal: boolean;
  notice: string;
}

export interface CodeReadinessParams {
  kind?: CodeKind | undefined;
  search?: string | undefined;
  payrollRunId?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export type CodeBulkAction = 'Create' | 'Replace' | 'Skip' | 'Conflict';

export interface CodeBulkRequest {
  codes: { kind: CodeKind; codeId: string }[];
  chartAccountId: string;
  effectiveFrom: string;
  replaceExisting: boolean;
}

export interface CodeBulkPreviewItem {
  kind: CodeKind;
  codeId: string;
  code: string | null;
  name: string | null;
  action: CodeBulkAction;
  existingMappingId: string | null;
  existingAccount: AccountRef | null;
  reason: string | null;
}

export interface CodeBulkPreview {
  items: CodeBulkPreviewItem[];
  create: number;
  replace: number;
  skip: number;
  conflict: number;
  errors: string[];
}

export interface CodeBulkApplyInput extends CodeBulkRequest {
  /** 1 to 100 characters of A-Z a-z 0-9 . _ : - so a retried request never applies twice. */
  idempotencyKey: string;
}

export interface CodeBulkResult {
  created: number;
  replaced: number;
  skipped: number;
  conflicts: number;
  /** True when this key had already been applied: nothing new was written. */
  replayed: boolean;
}

// ---- taxonomy (complete; payroll supply is reported by sourceCoverage) ----

export interface CategoryInfo {
  key: string;
  label: string;
  side: JournalSide;
  /** The account types that are the normal home for this category (a different one is only a warning). */
  hint: string;
}

/** Contract section 3: the whole taxonomy, in posting order. The taxonomy is complete; what payroll supplies is not. */
export const CATEGORIES: readonly CategoryInfo[] = [
  { key: 'EarningsGross', label: 'Gross wages', side: 'Debit', hint: 'An expense account' },
  { key: 'EmployerTaxExpense', label: 'Employer tax expense', side: 'Debit', hint: 'An expense account' },
  { key: 'EmployerBenefitExpense', label: 'Employer benefit expense', side: 'Debit', hint: 'An expense account' },
  { key: 'EmployerRetirementExpense', label: 'Employer retirement expense', side: 'Debit', hint: 'An expense account' },
  { key: 'OtherEmployerExpense', label: 'Other employer expense', side: 'Debit', hint: 'An expense account' },
  { key: 'EmployeeTaxWithholding', label: 'Employee tax withholding payable', side: 'Credit', hint: 'A liability account' },
  { key: 'EmployerTaxLiability', label: 'Employer tax liability payable', side: 'Credit', hint: 'A liability account' },
  { key: 'PreTaxDeductionPayable', label: 'Pre-tax deductions payable', side: 'Credit', hint: 'A liability account' },
  { key: 'PostTaxDeductionPayable', label: 'Post-tax deductions payable', side: 'Credit', hint: 'A liability account' },
  { key: 'GarnishmentPayable', label: 'Garnishments payable', side: 'Credit', hint: 'A liability account' },
  { key: 'LoanRepaymentReceivable', label: 'Loan repayments receivable', side: 'Credit', hint: 'A receivable (asset) account' },
  { key: 'ReimbursementPayable', label: 'Reimbursements payable', side: 'Credit', hint: 'A liability account' },
  { key: 'NetPayClearing', label: 'Net pay (Payroll Clearing)', side: 'Credit', hint: 'A Clearing or Liability account' },
];

export const categoryLabel = (key: string): string => CATEGORIES.find((c) => c.key === key)?.label ?? key;
export const categorySide = (key: string): JournalSide | null => CATEGORIES.find((c) => c.key === key)?.side ?? null;

export const exportStatusLabels: Record<string, string> = {
  None: 'Not exported',
  Draft: 'Draft',
  ValidationFailed: 'Blocked',
  Ready: 'Ready',
  Generated: 'Generated',
  Downloaded: 'Downloaded',
  Superseded: 'Superseded',
  ReversalRequired: 'Reversal required',
  Reversed: 'Reversed',
  Failed: 'Failed',
};

export const exportStatusLabel = (status: string): string => exportStatusLabels[status] ?? status;

export const exportKindLabels: Record<string, string> = { Original: 'Original', Reversal: 'Reversal', Replacement: 'Replacement' };
export const exportModeLabels: Record<string, string> = { Compound: 'Compound entry', PerEmployee: 'Per employee' };
